import React, { useEffect, useState } from "react";
import { api, fmtUSD, fmtNumber } from "../../lib/api";
import KpiCard from "../KpiCard";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  CloudCog,
  Layers,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LineChart,
  Line,
  CartesianGrid,
  ComposedChart,
  Area,
} from "recharts";

// Smart Y-axis money tick formatter that adapts to the value range.
const moneyTick = (v) => {
  const n = Math.abs(v);
  if (n >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${Math.round(v)}`;
};

export default function CEOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.ce
      .snapshot()
      .then((d) => !cancelled && setData(d))
      .catch((e) => console.error(e))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || !data) {
    return (
      <div className="py-6" data-testid="cloud-economics-loading">
        <div className="cs-card p-10 text-center text-muted-foreground">
          Loading Cloud Economics…
        </div>
      </div>
    );
  }

  const forecastUp = data.forecast.delta_pct >= 0;

  // BU monthly trend keys (exclude "month")
  const buKeys = data.by_business_unit.map((b) => b.name);
  // Category trend keys
  const catKeys = Array.from(
    new Set(
      data.product_category_trend.flatMap((r) => Object.keys(r).filter((k) => k !== "month"))
    )
  );
  const catColors = {
    Compute: "#00579e",
    Storage: "#15803d",
    Database: "#6d28d9",
    "Analytics & BigData": "#be185d",
    "AI / ML": "#9a3412",
    "Networking & CDN": "#0e7490",
    "Management & Logging": "#374151",
    "Security & Identity": "#f59e0b",
  };

  const dailyKeys = data.daily_cost_by_service.length
    ? Object.keys(data.daily_cost_by_service[0]).filter((k) => k !== "date")
    : [];
  const dailyColors = ["#00579e", "#15803d", "#6d28d9", "#be185d", "#9a3412", "#0e7490"];

  return (
    <div className="space-y-8" data-testid="cloud-economics-overview">
      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard
          testId="ce-kpi-current-month"
          label="Cost Expense (forecast)"
          value={fmtUSD(data.forecast.current_month_forecast_usd, { compact: true })}
          sublabel={`${forecastUp ? "+" : ""}${data.forecast.delta_pct}% vs last month`}
          icon={DollarSign}
          tone={forecastUp ? "danger" : "success"}
        />
        <KpiCard
          testId="ce-kpi-mtd"
          label="Spend month-to-date"
          value={fmtUSD(data.forecast.current_month_spend_mtd_usd || 0, { compact: true })}
          sublabel={`${data.forecast.month_progress_pct || 0}% through the month`}
          icon={DollarSign}
          tone="warning"
        />
        <KpiCard
          testId="ce-kpi-last-month"
          label="Last Month"
          value={fmtUSD(data.forecast.last_month_usd, { compact: true })}
          sublabel="actual closed spend"
          icon={TrendingDown}
          tone="primary"
        />
        <KpiCard
          testId="ce-kpi-annual"
          label="Annual Run-Rate"
          value={fmtUSD(data.forecast.annual_run_rate_usd, { compact: true })}
          sublabel="12× current month"
          icon={TrendingUp}
          tone="teal"
        />
        <KpiCard
          testId="ce-kpi-savings-potential"
          label="Savings Potential"
          value={fmtUSD(data.optimization_summary.monthly_savings_potential_usd, { compact: true })}
          sublabel={`${data.optimization_summary.open_recommendations} open recs · ${data.optimization_summary.auto_remediable_count} auto-fix`}
          icon={Sparkles}
          tone="success"
        />
      </div>

      {/* Cost by Business Unit + Prod-vs-Nonprod + Vendor split */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="cs-card p-6 lg:col-span-2" data-testid="ce-bu-trend-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="cs-heading text-base font-semibold tracking-tight">
                Cost by Business Unit
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Monthly spend trend by BU · last 6 months
              </p>
            </div>
            <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
              {data.by_business_unit.length} BUs
            </span>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.bu_monthly_trend} margin={{ left: 0, right: 12, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#52525B" />
                <YAxis
                  tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }}
                  stroke="#52525B"
                  tickFormatter={moneyTick}
                />
                <Tooltip contentStyle={{ borderRadius: 4, fontSize: 12 }} formatter={(v) => fmtUSD(v, { compact: true })} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
                {buKeys.map((k, i) => (
                  <Bar
                    key={k}
                    dataKey={k}
                    stackId="bu"
                    fill={data.by_business_unit[i]?.color || "#52525B"}
                    maxBarSize={48}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* BU breakdown table */}
          <div className="mt-4 space-y-1.5" data-testid="ce-bu-list">
            {data.by_business_unit.map((bu) => (
              <div
                key={bu.id}
                className="flex items-center gap-3 text-xs py-1.5 border-b border-border last:border-0"
                data-testid={`ce-bu-row-${bu.id}`}
              >
                <span className="w-2 h-2 rounded-sm" style={{ background: bu.color }} />
                <span className="font-medium text-foreground flex-1">{bu.name}</span>
                <span className="text-muted-foreground cs-mono">{bu.share_pct}%</span>
                <span
                  className={`cs-mono font-medium ${
                    bu.mom_delta_pct >= 0 ? "text-[#be185d]" : "text-[#15803d]"
                  }`}
                >
                  {bu.mom_delta_pct >= 0 ? "+" : ""}
                  {bu.mom_delta_pct}%
                </span>
                <span className="cs-mono font-semibold text-[#00579e] w-24 text-right">
                  {fmtUSD(bu.monthly_cost_usd, { compact: true })}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="cs-card p-6" data-testid="ce-prod-split-card">
            <h2 className="cs-heading text-base font-semibold tracking-tight">
              Prod vs Non-Prod
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5 mb-3">Cost split by environment</p>
            <div className="h-44">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={data.prod_split}
                    dataKey="value"
                    nameKey="env"
                    innerRadius={42}
                    outerRadius={70}
                    paddingAngle={2}
                  >
                    {data.prod_split.map((d, i) => (
                      <Cell key={i} fill={d.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: 4, fontSize: 12 }}
                    formatter={(v) => fmtUSD(v, { compact: true })}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-1 mt-1">
              {data.prod_split.map((p) => (
                <div key={p.env} className="flex items-center gap-2 text-xs">
                  <span className="w-2 h-2 rounded-sm" style={{ background: p.color }} />
                  <span className="text-muted-foreground flex-1">{p.env}</span>
                  <span className="cs-mono font-medium">{fmtUSD(p.value, { compact: true })}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="cs-card p-6" data-testid="ce-vendor-card">
            <div className="flex items-start justify-between mb-1">
              <h2 className="cs-heading text-base font-semibold tracking-tight">Vendor Cost Split</h2>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 mb-3">Spend by cloud provider</p>
            <div className="h-40">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={data.by_vendor.filter((v) => v.monthly_cost_usd > 0)}
                    dataKey="monthly_cost_usd"
                    nameKey="name"
                    innerRadius={36}
                    outerRadius={64}
                    paddingAngle={2}
                  >
                    {data.by_vendor
                      .filter((v) => v.monthly_cost_usd > 0)
                      .map((d, i) => (
                        <Cell key={i} fill={d.color} />
                      ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: 4, fontSize: 12 }}
                    formatter={(v) => fmtUSD(v, { compact: true })}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-1 mt-1">
              {data.by_vendor.map((v) => (
                <div
                  key={v.id}
                  className={`flex items-center gap-2 text-xs ${v.monthly_cost_usd === 0 ? "opacity-50" : ""}`}
                  data-testid={`ce-vendor-${v.id}`}
                >
                  <span className="w-2 h-2 rounded-sm" style={{ background: v.color }} />
                  <span className="text-muted-foreground flex-1">{v.name}</span>
                  <span className="cs-mono font-medium">
                    {v.monthly_cost_usd > 0 ? fmtUSD(v.monthly_cost_usd, { compact: true }) : "$0"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Monthly usage by product category */}
      <div className="cs-card p-6" data-testid="ce-product-category-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="cs-heading text-base font-semibold tracking-tight">
              Monthly Usage by Product Category
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Stacked spend by service category · last 6 months
            </p>
          </div>
          <Layers className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="h-64">
          <ResponsiveContainer>
            <BarChart data={data.product_category_trend} margin={{ left: 0, right: 12, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#52525B" />
              <YAxis
                tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }}
                stroke="#52525B"
                tickFormatter={moneyTick}
              />
              <Tooltip contentStyle={{ borderRadius: 4, fontSize: 12 }} formatter={(v) => fmtUSD(v, { compact: true })} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
              {catKeys.map((k) => (
                <Bar key={k} dataKey={k} stackId="cat" fill={catColors[k] || "#52525B"} maxBarSize={48} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Spending Drivers + Daily cost by service */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="cs-card p-0 overflow-hidden" data-testid="ce-drivers-card">
          <div className="p-6 pb-3 border-b border-border">
            <h2 className="cs-heading text-base font-semibold tracking-tight">
              Top Spending Drivers
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Top 10 services across all providers · current month
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
              <tr className="text-left">
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">
                  Service
                </th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">
                  Vendor
                </th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">
                  Spend
                </th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">
                  MoM
                </th>
              </tr>
            </thead>
            <tbody>
              {data.top_spending_drivers.map((d, i) => (
                <tr key={i} className="cs-row-hover border-b border-border last:border-0" data-testid={`ce-driver-row-${i}`}>
                  <td className="px-4 py-2.5 font-medium">{d.service}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{d.vendor}</td>
                  <td className="px-4 py-2.5 text-right cs-mono font-semibold text-[#00579e]">
                    {fmtUSD(d.monthly_cost_usd, { compact: true })}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <span
                      className={`cs-mono inline-flex items-center gap-0.5 text-xs ${
                        d.mom_delta_pct >= 0 ? "text-[#be185d]" : "text-[#15803d]"
                      }`}
                    >
                      {d.mom_delta_pct >= 0 ? (
                        <ArrowUpRight className="w-3 h-3" />
                      ) : (
                        <ArrowDownRight className="w-3 h-3" />
                      )}
                      {Math.abs(d.mom_delta_pct)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="cs-card p-6" data-testid="ce-daily-cost-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="cs-heading text-base font-semibold tracking-tight">
                Daily Cost by Service
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Top 6 services · last 90 days
              </p>
            </div>
            <CloudCog className="w-4 h-4 text-muted-foreground" />
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <LineChart data={data.daily_cost_by_service} margin={{ left: 0, right: 12, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }}
                  stroke="#52525B"
                  interval={10}
                />
                <YAxis
                  tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }}
                  stroke="#52525B"
                  tickFormatter={(v) => (v < 1 ? `$${v.toFixed(2)}` : `$${Math.round(v)}`)}
                />
                <Tooltip contentStyle={{ borderRadius: 4, fontSize: 12 }} formatter={(v) => fmtUSD(v, { compact: true })} />
                <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
                {dailyKeys.map((k, i) => (
                  <Line
                    key={k}
                    type="monotone"
                    dataKey={k}
                    stroke={dailyColors[i % dailyColors.length]}
                    strokeWidth={1.5}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Storage class + compute usage hours vs cost */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="cs-card p-6" data-testid="ce-storage-class-card">
          <h2 className="cs-heading text-base font-semibold tracking-tight">
            Cost by Storage Class
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5 mb-3">
            Stacked spend by tier · current month
          </p>
          <div className="h-48">
            <ResponsiveContainer>
              <BarChart data={data.storage_class_breakdown} layout="vertical" margin={{ left: 12, right: 12 }}>
                <XAxis type="number" tick={{ fontSize: 10 }} stroke="#52525B" tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v)}`)} />
                <YAxis
                  dataKey="name"
                  type="category"
                  tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }}
                  stroke="#52525B"
                  width={120}
                />
                <Tooltip contentStyle={{ borderRadius: 4, fontSize: 12 }} formatter={(v) => fmtUSD(v, { compact: true })} />
                <Bar dataKey="value" radius={[0, 3, 3, 0]} maxBarSize={18}>
                  {data.storage_class_breakdown.map((d, i) => (
                    <Cell key={i} fill={d.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="cs-card p-6 lg:col-span-2" data-testid="ce-compute-dual-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="cs-heading text-base font-semibold tracking-tight">
                Compute Usage Hours vs Cost
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Dual-axis trend · last 60 days
              </p>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer>
              <ComposedChart data={data.compute_usage_cost_dual} margin={{ left: 0, right: 12, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#52525B" interval={6} />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 11 }}
                  stroke="#52525B"
                  tickFormatter={(v) => `${Math.round(v / 1000)}k hr`}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 11 }}
                  stroke="#00579e"
                  tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v)}`)}
                />
                <Tooltip contentStyle={{ borderRadius: 4, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="usage_hours"
                  fill="#15803d"
                  fillOpacity={0.18}
                  stroke="#15803d"
                  strokeWidth={1.5}
                  name="Usage hours"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="cost_usd"
                  stroke="#00579e"
                  strokeWidth={2}
                  dot={false}
                  name="Cost ($)"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recommendation Categories (PRD spec) */}
      {data.recommendation_categories?.length > 0 && (
        <div className="cs-card p-0 overflow-hidden" data-testid="ce-rec-categories-card">
          <div className="p-6 pb-3 border-b border-border flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="cs-heading text-base font-semibold tracking-tight">
                Recommendation Categories
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Current vs. projected monthly spend, by optimization category
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div>
                <span className="text-muted-foreground">Total savings target:</span>{" "}
                <span className="cs-mono font-semibold text-[#15803d]">
                  {fmtUSD(
                    data.recommendation_categories.reduce((s, r) => s + r.target_savings, 0),
                    { compact: true }
                  )}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Projected spend:</span>{" "}
                <span className="cs-mono font-semibold text-[#00579e]">
                  {fmtUSD(data.optimization_summary.projected_monthly_spend_usd || 0, { compact: true })}
                </span>
              </div>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
              <tr className="text-left">
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">
                  Recommendation
                </th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">
                  Category
                </th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">
                  Current
                </th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">
                  Projected
                </th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">
                  Savings
                </th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">
                  %
                </th>
              </tr>
            </thead>
            <tbody>
              {data.recommendation_categories.map((r) => (
                <tr
                  key={r.id}
                  className="cs-row-hover border-b border-border last:border-0"
                  data-testid={`ce-rec-row-${r.id}`}
                >
                  <td className="px-4 py-2.5 font-medium text-foreground">{r.name}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{r.category}</td>
                  <td className="px-4 py-2.5 text-right cs-mono">{fmtUSD(r.current_spend, { compact: true })}</td>
                  <td className="px-4 py-2.5 text-right cs-mono">{fmtUSD(r.projected_spend, { compact: true })}</td>
                  <td className="px-4 py-2.5 text-right cs-mono font-semibold text-[#15803d]">
                    -{fmtUSD(r.target_savings, { compact: true })}
                  </td>
                  <td className="px-4 py-2.5 text-right cs-mono text-xs text-[#15803d]">
                    -{r.savings_pct}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Optimization summary tile */}
      <div className="cs-card p-6 flex items-center gap-6 flex-wrap" data-testid="ce-optimization-card">
        <Sparkles className="w-8 h-8 text-[#00579e]" />
        <div className="flex-1 min-w-[200px]">
          <h2 className="cs-heading text-base font-semibold tracking-tight">
            Optimization Recommendations
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Pulled from the latest FinOps scan across all connected providers
          </p>
        </div>
        <div className="text-right">
          <div className="cs-mono text-xl font-semibold text-[#00579e]">
            {fmtNumber(data.optimization_summary.open_recommendations)}
          </div>
          <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
            open
          </div>
        </div>
        <div className="text-right">
          <div className="cs-mono text-xl font-semibold text-[#15803d]">
            {fmtUSD(data.optimization_summary.monthly_savings_potential_usd, { compact: true })}
          </div>
          <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
            potential / mo
          </div>
        </div>
        <div className="text-right">
          <div className="cs-mono text-xl font-semibold text-[#6d28d9]">
            {fmtNumber(data.optimization_summary.auto_remediable_count)}
          </div>
          <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
            auto-fix
          </div>
        </div>
      </div>
    </div>
  );
}
